import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Tooltip, Card, Space, Button, Input, Checkbox } from 'antd';
import { VariableSizeList as List, areEqual } from 'react-window';
import clsx from 'clsx';
import Icon from 'component/icon';
import { compareArrays } from 'lib';
import { TableContext } from './context';

const HeaderTitle = (props) => {
    const { col } = props;
    const {
        editedData,
        headerTooltip,
        sortInfo,
        setSortInfo,
        filterInfo,
        setFilterInfo,
        langConfig,
        handleFilterColumn
    } = useContext(TableContext);

    const [searchText, setSearchText] = useState('');
    const options = useMemo(() => {
        const addonSet = new Set(); // 遇到 Array 會需要拆分，先存在這
        const resultSet = new Set(
            editedData?.map((row, index) => {
                let text = row[col.dataIndex];
                if (typeof text === 'object' && text) {
                    if (Array.isArray(text) && text.length > 0) {
                        const newText = text[0];
                        text.forEach((t) => (
                            addonSet.add(col.filterRender ? col.filterRender(t, row, index) : t)
                        ));
                        text = newText;
                    } else {
                        text = JSON.stringify(text);
                    }
                }
                return col.filterRender ? col.filterRender(text, row, index) : text;
            }) || []
        );
        let result = [...new Set([...resultSet, ...addonSet])];
        result = result.filter((option) => !!option || option === 0);
        if (searchText) {
            result = result.filter((option) => option.toString().includes(searchText));
        }
        return result;
    }, [col, editedData, searchText]);
    const onClickSort = (orderBy) => {
        if (sortInfo?.dataIndex === col.dataIndex && sortInfo?.orderBy === orderBy) {
            setSortInfo({});
        } else {
            setSortInfo?.({ dataIndex: col.dataIndex, orderBy });
        }
    };
    const onClickReset = () => {
        setFilterInfo((prev) => {
            const newInfo = {
                ...prev,
                [col.dataIndex]: col.defaultFilter || []
            };
            if (handleFilterColumn) {
                handleFilterColumn(newInfo);
            }
            return newInfo;
        });
    };
    const onClickSelectAll = () => {
        setFilterInfo((prev) => {
            const newInfo = {
                ...prev,
                [col.dataIndex]: options
            };
            if (handleFilterColumn) {
                handleFilterColumn(newInfo);
            }
            return newInfo;
        });
    };
    const onChangeFilter = (option) => {
        setFilterInfo((prev) => {
            let result = prev[col.dataIndex] || [];
            result = result.includes(option) ?
                result.filter((item) => item !== option) : [...result, option];
            const newInfo = { ...prev, [col.dataIndex]: result };
            if (handleFilterColumn) {
                handleFilterColumn(newInfo);
            }
            return newInfo;
        });
    };

    useEffect(() => {
        if (col.defaultFilter) {
            setFilterInfo((prev) => {
                const isArraysEqual = compareArrays(prev[col.dataIndex], col.defaultFilter);
                if (!isArraysEqual) {
                    return { ...prev, [col.dataIndex]: col.defaultFilter };
                }
                return prev;
            });
        }
    }, [col, setFilterInfo]);
    useEffect(() => {
        const newValue = [];
        const deletedValue = [];
        filterInfo[col.dataIndex]?.forEach((item) => {
            if (options.includes(item) || col.defaultFilter?.includes(item)) {
                newValue.push(item);
            } else {
                deletedValue.push(item);
            }
        });
        if (deletedValue.length) {
            setFilterInfo((prev) => {
                const newInfo = { ...prev, [col.dataIndex]: newValue };
                if (handleFilterColumn) {
                    handleFilterColumn(newInfo);
                }
                return newInfo;
            });
        }
    }, [options, col, filterInfo, setFilterInfo, handleFilterColumn]);

    return (
        <Tooltip
            destroyTooltipOnHide={{ keepParent: false }}
            placement="bottomLeft"
            trigger={headerTooltip && col.showTooltip !== false ? 'click' : ''}
            overlayClassName="table-tooltip"
            title={(
                <Card className="text-sm mt-1 w-[200px]">
                    <Space>
                        <span>{langConfig?.sort || '排序'}</span>
                        <Button
                            size="small"
                            className={clsx(
                                'w-6 h-6 text-gray-dark01',
                                {
                                    'bg-primary':
                                        col.dataIndex === sortInfo?.dataIndex &&
                                        sortInfo?.orderBy === 'asc'
                                }
                            )}
                            icon={(
                                <Icon
                                    name="arrow_drop_up"
                                    size={24}
                                    className={clsx(
                                        {
                                            'text-white':
                                                col.dataIndex === sortInfo?.dataIndex &&
                                                sortInfo?.orderBy === 'asc'
                                        }
                                    )}
                                />
                            )}
                            onClick={() => onClickSort('asc')}
                        />
                        <Button
                            size="small"
                            className={clsx(
                                'w-6 h-6 text-gray-dark01',
                                {
                                    'bg-primary':
                                        col.dataIndex === sortInfo?.dataIndex &&
                                        sortInfo?.orderBy === 'desc'
                                }
                            )}
                            icon={(
                                <Icon
                                    name="arrow_drop_down"
                                    size={24}
                                    className={clsx(
                                        {
                                            'text-white': col.dataIndex ===
                                                sortInfo?.dataIndex && sortInfo?.orderBy === 'desc'
                                        }
                                    )}
                                />
                            )}
                            onClick={() => onClickSort('desc')}
                        />
                    </Space>
                    <div className="table-tooltip-divider my-2" />
                    <div>{langConfig?.filter || '篩選'}</div>
                    <Input
                        size="small"
                        prefix={<Icon name="search" className="text-table-tab-selected" />}
                        className="mt-1 py-0.5 rounded-md"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                    <div className="flex my-1">
                        <Button
                            size="small"
                            className="text-black w-1/2 mr-1"
                            onClick={onClickReset}
                        >
                            {langConfig?.reset || '重置'}
                        </Button>
                        <Button
                            size="small"
                            className="text-black w-1/2"
                            onClick={onClickSelectAll}
                        >
                            {langConfig?.selectAll || '全選'}
                        </Button>
                    </div>
                    <List
                        width={180}
                        height={150}
                        itemCount={options.length}
                        itemSize={() => 24}
                    >
                        {React.memo(({ style, index }) => (
                            <div style={style}>
                                <Checkbox
                                    checked={filterInfo?.[col.dataIndex]?.includes(options[index])}
                                    onChange={() => onChangeFilter(options[index])}
                                    className="whitespace-nowrap"
                                >
                                    <span
                                        className={clsx(
                                            {
                                                'text-primary':
                                                    filterInfo?.[col.dataIndex]
                                                        ?.includes(options[index])
                                            }
                                        )}
                                    >
                                        {options[index]}
                                    </span>
                                </Checkbox>
                            </div>
                        ), areEqual)}
                    </List>
                </Card>
            )}
        >
            <div
                className={clsx(
                    'flex justify-center items-center h-full rounded-md',
                    'font-bold text-gray-dark02',
                    {
                        'cursor-pointer hover:text-white hover:bg-gray-dark01':
                            headerTooltip && col.showTooltip !== false
                    }
                )}
            >
                {sortInfo?.dataIndex === col.dataIndex && (
                    sortInfo.orderBy === 'asc' ? (
                        <Icon name="arrow_drop_up" className="text-primary scale-150" />
                    ) : (
                        <Icon name="arrow_drop_down" className="text-primary scale-150" />
                    )
                )}
                {!!filterInfo?.[col.dataIndex]?.length && (
                    <Icon name="filter_alt" className="text-primary" />
                )}
                {col.title}
            </div>
        </Tooltip>
    );
};

export default HeaderTitle;
